import type { ReactElement } from "react";
import { LIMB_IDS } from "@rietz/shared";
import { LIMB_BOX_H, LIMB_BOX_W, LIMB_LABELS, LIMB_LAYOUT, limbColor } from "../limbs.js";
import { useHud } from "../store.js";
import { PANEL_LINE, TEXT, TEXT_DIM } from "../theme.js";

/**
 * The Health tab: the HUD's silhouette three times larger, beside a table of
 * `current/max` per limb with conditions spelled out. Everything comes off the
 * store's snapshot fields — the UI draws what the server said and never
 * re-computes it.
 */

/** How much bigger than the HUD's corner widget the overview body is drawn. */
const SCALE = 3;

export function HealthPane(): ReactElement {
  const limbs = useHud((s) => s.limbs);
  const maxLimbs = useHud((s) => s.maxLimbs);
  const bleedMask = useHud((s) => s.bleedMask);
  const fracMask = useHud((s) => s.fracMask);

  const rows = LIMB_IDS.map((id, i) => {
    const hp = limbs[i] ?? 0;
    const max = maxLimbs[i] ?? 1;
    return {
      id,
      i,
      label: LIMB_LABELS[i] ?? id,
      hp,
      max,
      frac: hp / (max > 0 ? max : 1),
      bleeding: (bleedMask & (1 << i)) !== 0,
      fractured: (fracMask & (1 << i)) !== 0,
    };
  });
  const total = rows.reduce((sum, r) => sum + r.hp, 0);
  const totalMax = rows.reduce((sum, r) => sum + r.max, 0);

  return (
    <div style={{ display: "flex", gap: 24, alignItems: "flex-start", padding: "6px 4px" }}>
      {/* The body. A strict enlargement of the HUD's widget, so a limb that is
          black in the corner is black here too. */}
      <div
        style={{
          position: "relative",
          width: LIMB_BOX_W * SCALE,
          height: LIMB_BOX_H * SCALE,
          flex: "0 0 auto",
        }}
      >
        {LIMB_LAYOUT.map((rect, i) => {
          const row = rows[i];
          if (row === undefined) return null;
          return (
            <div
              key={row.id}
              className={
                "limb" + (row.bleeding ? " limb-bleed" : "") + (row.fractured ? " limb-frac" : "")
              }
              title={`${row.label} ${Math.round(row.hp)}/${Math.round(row.max)}`}
              style={{
                position: "absolute",
                left: rect.left * SCALE,
                top: rect.top * SCALE,
                width: rect.width * SCALE,
                height: rect.height * SCALE,
                background: limbColor(row.frac),
                display: "grid",
                placeItems: "center",
                fontSize: 10,
                color: row.frac > 0 ? "rgba(0, 0, 0, 0.8)" : TEXT_DIM,
                textShadow: "none",
              }}
            >
              {Math.round(row.hp)}
            </div>
          );
        })}
      </div>

      {/* The table. One line per limb in wire order, the total at the foot. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto 120px auto auto",
          columnGap: 12,
          rowGap: 8,
          alignItems: "center",
          minWidth: 340,
        }}
      >
        {rows.map((row) => (
          <LimbRow key={row.id} {...row} />
        ))}
        <div style={{ gridColumn: "1 / -1", borderTop: `1px solid ${PANEL_LINE}` }} />
        <span style={{ color: TEXT }}>Total</span>
        <Bar frac={totalMax > 0 ? total / totalMax : 0} />
        <span style={{ color: TEXT, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          {Math.round(total)}/{Math.round(totalMax)}
        </span>
        <span />
      </div>
    </div>
  );
}

interface LimbRowProps {
  label: string;
  hp: number;
  max: number;
  frac: number;
  bleeding: boolean;
  fractured: boolean;
}

function LimbRow(props: LimbRowProps): ReactElement {
  const blacked = !(props.hp > 0);
  return (
    <>
      <span style={{ color: blacked ? TEXT_DIM : TEXT }}>{props.label}</span>
      <Bar frac={props.frac} />
      <span
        style={{
          color: blacked ? TEXT_DIM : TEXT,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {Math.round(props.hp)}/{Math.round(props.max)}
      </span>
      <span style={{ display: "flex", gap: 4, minHeight: 14 }}>
        {blacked ? <Tag color="rgba(120, 120, 120, 0.9)">destroyed</Tag> : null}
        {props.bleeding ? <Tag color="rgba(220, 40, 40, 0.9)">bleeding</Tag> : null}
        {props.fractured ? <Tag color="rgba(242, 242, 242, 0.8)">fractured</Tag> : null}
      </span>
    </>
  );
}

function Bar(props: { frac: number }): ReactElement {
  const f = props.frac > 1 ? 1 : props.frac > 0 ? props.frac : 0;
  return (
    <span
      style={{
        display: "block",
        height: 8,
        borderRadius: 2,
        border: `1px solid ${PANEL_LINE}`,
        background: "rgba(0, 0, 0, 0.45)",
        overflow: "hidden",
      }}
    >
      <span
        style={{
          display: "block",
          height: "100%",
          width: `${(f * 100).toFixed(1)}%`,
          background: limbColor(f),
        }}
      />
    </span>
  );
}

function Tag(props: { color: string; children: string }): ReactElement {
  return (
    <span
      style={{
        fontSize: 10,
        letterSpacing: "0.06em",
        padding: "1px 5px",
        borderRadius: 2,
        border: `1px solid ${props.color}`,
        color: props.color,
        whiteSpace: "nowrap",
      }}
    >
      {props.children}
    </span>
  );
}
